import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { TableModule } from 'primeng/table';
import { TooltipModule } from 'primeng/tooltip';
import { InputTextModule } from 'primeng/inputtext';
import { Toast } from '@/shared/components/toast/toast';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ConfirmationService, MessageService } from 'primeng/api';

import { CardContainer } from '@/shared/components/card-container/card-container';
import { CardContent } from '@/shared/components/card-content/card-content';
import { TableViewSwitcherComponent } from '@/shared/components/layout/table-view-switcher/table-view-switcher.component';
import { SelectEstado } from '@/shared/components/layout/select-estado/select-estado';
import { LayoutService } from '@/layout/service/layout.service';
import { DashboardPerfilService } from '@/features/portal/dashboard-profile/dashboard-profile.service';
import { ModuloAdminService } from '@/core/api/sicom-api/portal/modulo/modulo-admin.service';
import { ModuloAdmin, ModuloUfItem } from '@/core/api/sicom-api/portal/modulo/modulo-admin.model';
import { CanDirective } from '@/core/permissions/can.directive';
import { PERMISSOES } from '@/core/permissions/permissoes.catalog';
import { PermissionService } from '@/core/permissions/permission.service';

import { ModuloDetalhe } from './componentes/modulo-detalhe/modulo-detalhe';
import { FiltroCampo } from '@/shared/components/layout/filtros-bar/filtro-campo';
import { FiltrosBar } from '@/shared/components/layout/filtros-bar/filtros-bar';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: 'app-modulos',
    standalone: true,
    imports: [
        FormsModule,
        ButtonModule,
        TableModule,
        TooltipModule,
        InputTextModule,
        Toast,
        ToggleSwitchModule,
        ConfirmDialogModule,
        CardContainer,
        CardContent,
        TableViewSwitcherComponent,
        SelectEstado,
        ModuloDetalhe,
        CanDirective,
        FiltrosBar,
        FiltroCampo
    ],
    providers: [MessageService, ConfirmationService],
    templateUrl: './modulos.html',
    styleUrl: './modulos.scss'
})
/**
 * Configurações → Gerais → Módulos: habilita/desabilita, por UF, os módulos exibidos no portal (WEB_MODULO).
 *
 * Toggle em modo staged: nenhuma alteração é persistida ao clicar no switch da linha — ela só fica marcada
 * como pendente em {@link alterados} (Map idWebModulo → novo valor). "Salvar" envia o lote pendente de uma vez
 * (`ModuloAdminService.salvarLoteUf`); "Cancelar" descarta e recarrega do servidor. Voltar um toggle ao valor
 * original remove a pendência (não é preciso "desfazer" — o estado original já não conta como alterado).
 */
export class Modulos implements OnInit {
    private readonly moduloService = inject(ModuloAdminService);
    private readonly perfilService = inject(DashboardPerfilService);
    private readonly messageService = inject(MessageService);
    private readonly confirmationService = inject(ConfirmationService);
    private readonly permissionService = inject(PermissionService);
    private readonly destroyRef = inject(DestroyRef);
    private readonly layoutService = inject(LayoutService);

    protected readonly roleModulosVisualizar = PERMISSOES.configuracoes.modulos.visualizar;
    protected readonly roleModulosEditar = PERMISSOES.configuracoes.modulos.editar;

    protected readonly viewMode = signal<'table' | 'card'>(this.layoutService.getPresentationType());

    protected readonly uf = signal<string>('');

    /** Regional não escolhe UF: o backend impõe a dele, e o select reflete isso em vez de mentir. */
    protected readonly ufTravada = computed(() => !this.permissionService.isCfm);

    /**
     * Editar nome/descrição mexe no catálogo global (WEB_MODULO não tem UF, então vale para todas
     * elas) e o backend recusa isso ao operador regional. A tela deixa de oferecer a ação em vez de
     * levá-lo até o 403. O toggle por UF — o que ele de fato administra — segue só com a role.
     */
    protected readonly podeEditarCatalogo = computed(() => this.permissionService.isCfm);

    protected readonly modulos = signal<ModuloAdmin[]>([]);
    protected readonly carregando = signal(false);
    protected readonly salvando = signal(false);

    /** Pendências do toggle em lote: idWebModulo → novo valor (ainda não persistido). */
    protected readonly alterados = signal<Map<number, boolean>>(new Map());
    protected readonly qtdAlteradas = computed(() => this.alterados().size);

    protected readonly filtroInput = signal('');
    protected readonly filtro = signal('');

    /** Modal de edição de nome/descrição do módulo. */
    protected readonly moduloEmEdicao = signal<ModuloAdmin | null>(null);
    protected readonly edicaoVisivel = signal(false);

    /** Módulos após busca por nome/descrição (client-side), ordenados por nome. */
    protected readonly itens = computed<ModuloAdmin[]>(() => {
        const termo = this.filtro().trim().toLowerCase();
        const lista = termo ? this.modulos().filter((m) => `${m.nmModulo} ${m.dsModulo}`.toLowerCase().includes(termo)) : this.modulos();
        return [...lista].sort((a, b) => a.nmModulo.localeCompare(b.nmModulo, 'pt-BR', { sensitivity: 'base' }));
    });

    /** Token para descartar respostas fora de ordem. */
    private requisicaoAtual = 0;

    ngOnInit(): void {
        // Regional: a UF do operador já é conhecida sem ida ao servidor — a tela inicia nela, e o perfil não a muda.
        const ufOperador = this.ufOperador();
        if (ufOperador) this.uf.set(ufOperador);

        this.perfilService
            .buscarPerfil()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (res) => {
                    const perfil = Array.isArray(res) ? res[0] : res;
                    const uf = ufOperador || (perfil?.uf ?? '').trim().toUpperCase();
                    this.uf.set(uf);
                    this.carregar();
                },
                error: () => this.carregar()
            });
    }

    /** UF imposta ao operador regional; `''` para o CFM, que escolhe livremente. */
    private ufOperador(): string {
        return this.ufTravada() ? (this.permissionService.ufUsuario ?? '').trim().toUpperCase() : '';
    }

    protected aplicarFiltro(): void {
        this.filtro.set(this.filtroInput());
    }

    protected limparFiltro(): void {
        this.filtroInput.set('');
        this.filtro.set('');
    }

    /** Troca de UF no seletor. Com pendências, confirma o descarte antes; sem pendências, troca direto. */
    protected onSelecionarUf(novaUf: string): void {
        const uf = (novaUf ?? '').trim().toUpperCase();
        if (!uf || uf === this.uf()) return;

        if (this.qtdAlteradas() > 0) {
            this.confirmationService.confirm({
                header: 'Descartar alterações?',
                message: 'Há alterações de módulos ainda não salvas nesta UF. Deseja descartá-las e trocar de UF?',
                icon: 'pi pi-exclamation-triangle',
                acceptLabel: 'Descartar e trocar',
                rejectLabel: 'Cancelar',
                acceptButtonStyleClass: 'p-button-danger',
                accept: () => {
                    this.alterados.set(new Map());
                    this.uf.set(uf);
                    this.carregar();
                }
            });
            return;
        }

        this.uf.set(uf);
        this.carregar();
    }

    /** Valor exibido no toggle da linha: o pendente em `alterados`, senão o estado atual na UF. */
    protected estadoAtual(m: ModuloAdmin): boolean {
        const alterados = this.alterados();
        return alterados.has(m.idWebModulo) ? !!alterados.get(m.idWebModulo) : m.habilitadoNaUf;
    }

    protected estaAlterado(m: ModuloAdmin): boolean {
        return this.alterados().has(m.idWebModulo);
    }

    /** Marca/desmarca a pendência da linha. Voltar ao valor original da UF remove a pendência. */
    protected onToggle(m: ModuloAdmin, novoValor: boolean): void {
        const novo = new Map(this.alterados());
        if (novoValor === m.habilitadoNaUf) {
            novo.delete(m.idWebModulo);
        } else {
            novo.set(m.idWebModulo, novoValor);
        }
        this.alterados.set(novo);
    }

    /** Envia o lote pendente. No sucesso, recarrega do servidor (zera pendências) e notifica. */
    protected salvar(): void {
        const itens: ModuloUfItem[] = [...this.alterados()].map(([idWebModulo, ativo]) => ({ idWebModulo, ativo }));
        if (!itens.length) return;

        this.salvando.set(true);
        this.moduloService
            .salvarLoteUf(this.uf(), itens)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: () => {
                    this.salvando.set(false);
                    this.messageService.add({ severity: 'success', summary: 'Sucesso', detail: 'Módulos atualizados com sucesso.' });
                    this.alterados.set(new Map());
                    this.carregar();
                },
                error: () => {
                    this.salvando.set(false);
                }
            });
    }

    /** Descarta as pendências e volta a refletir o que está salvo no servidor. */
    protected cancelar(): void {
        this.alterados.set(new Map());
        this.carregar();
    }

    /** Abre o modal de edição de nome/descrição do módulo. */
    protected abrirEdicao(m: ModuloAdmin): void {
        this.moduloEmEdicao.set(m);
        this.edicaoVisivel.set(true);
    }

    /** Após salvar no modal: fecha e recarrega (nome/descrição são globais — a lista da UF atual precisa refletir). */
    protected onModuloSalvo(): void {
        this.edicaoVisivel.set(false);
        this.carregar();
    }

    protected carregar(): void {
        // Sem leitura de módulos: não busca (evita 403 → toast); mostra vazio.
        if (!this.permissionService.can(this.roleModulosVisualizar)) {
            this.modulos.set([]);
            this.alterados.set(new Map());
            this.carregando.set(false);
            return;
        }

        const uf = this.uf();
        if (!uf) {
            this.modulos.set([]);
            this.alterados.set(new Map());
            this.carregando.set(false);
            return;
        }

        this.carregando.set(true);
        const requisicao = ++this.requisicaoAtual;
        this.moduloService
            .listarPorUf(uf)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (lista) => {
                    if (requisicao !== this.requisicaoAtual) return;
                    this.modulos.set(lista ?? []);
                    this.alterados.set(new Map());
                    this.carregando.set(false);
                },
                error: () => {
                    if (requisicao !== this.requisicaoAtual) return;
                    this.modulos.set([]);
                    this.carregando.set(false);
                }
            });
    }
}
